import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ViewportPortal } from "./shared/ViewportDialog";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { CPTextarea } from "./CPTextarea";
import { supabase } from "../supabase";
import {
  computeLineLabourCost,
  isRateMissing,
  normalizeLogHours,
} from "../helpers/labourCost";
import {
  attachTimelogEntryDates,
  groupProjectMaterialUsageForDisplay,
  insertManualMaterialUsage,
  loadMaterialUsageForProject,
  voidMaterialUsage,
} from "../helpers/projectMaterialLedger";
import { fetchAndCacheMaterialsCatalog } from "../helpers/materialsCatalog";
import { loadUsersByAuthIdsForOrg } from "../helpers/scopedQueries";
import {
  buildLabourTotals,
  loadLabourForProject,
} from "../helpers/projectLabourLedger";
import {
  computeActualCostFromLedgers,
  loadProjectFinancialSummary,
  roundCurrency,
} from "../helpers/projectFinancialReport";
import {
  addProjectOtherCost,
  loadProjectOtherCosts,
  voidProjectOtherCost,
} from "../helpers/projectOtherCosts";
import { ProjectFinancialPanel } from "./ProjectFinancialPanel";
import { ProjectLocationWorkspace } from "./project/ProjectLocationWorkspace";
import { ProjectOtherCostsPane } from "./project/ProjectOtherCostsPane";
import { ProjectWorkspaceHome } from "./project/ProjectWorkspaceHome";
import { ProjectPicturesScreen } from "./project/ProjectPicturesScreen";
import { InvoiceListCard } from "./invoice/InvoiceListCard";
import { loadInvoices } from "../helpers/invoices";
import { loadProjectPictureSummaries } from "../helpers/projectPictures";
import { countPendingPictures } from "../helpers/pictureSync";
import { countOfflineProjectPictures } from "../helpers/projectFavorites";
import { RecIcon, RecThumb } from "./materials/MaterialRecordParts";
import { TimeLogList } from "./timelogs/TimeLogList";
import { useProjectFavorite } from "../hooks/useProjectFavorite";
import { ProjectTasksScreen } from "./tasks/ProjectTasksScreen";
import { ProjectConversationsScreen } from "./project/ProjectConversationsScreen";
import { isTasksUnavailableError, loadProjectTasks } from "../helpers/tasks/projectTasks";
import { taskCountLabel } from "../helpers/tasks/taskModel";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { ProjectDesktopOverview } from "./project/ProjectDesktopOverview";
import { formatTargetCompletion, loadProjectStructure } from "../helpers/projectStructure";
import { summarizeProjectTasks } from "../helpers/dashboard/commandDashboardModel";
import { localTodayISO } from "../helpers/planning/planningModel";
import { ClientViewManageScreen } from "./clientView/ClientViewManageScreen";
import { getProjectShare } from "../helpers/clientView/clientViewShare";
import { isMissingRpcError } from "../helpers/clientView/clientViewModel";
import "./materials/materialRecord.css";
import "./project/projects.css";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import { blockDemoSave, shouldSkipSupabase } from "../helpers/guestMode";
import { isOffline } from "../helpers/connectivity";
import { loadFieldProjectRecord } from "../helpers/projectFieldSnapshot";
import {
  getMaterialRelationName,
  stripMaterialPricingFields,
} from "../helpers/materialVisibility";
import {
  applyOrganizationScope,
  resolveOrganizationIdForQueries,
} from "../helpers/orgQueryScope";
import {
  canAccessProjectFinancialData,
  getProjectsRelationName,
} from "../helpers/operationalProjections";
import { isProjectArchived } from "../helpers/projectVisibility";
import "./ProjectDetailScreen.css";

const PROJECT_SELECT_FINANCIAL = `
  project_id,
  project_name,
  project_number,
  site_address,
  estimated_end_date,
  status,
  budget_amount,
  notes,
  organization_id,
  client_id,
  clients ( client_name )
`;

const PROJECT_SELECT_OPERATIONAL = `
  project_id,
  project_name,
  project_number,
  site_address,
  estimated_end_date,
  status,
  notes,
  organization_id,
  client_id,
  client_name
`;

function formatHoursMinutes(decimalHours) {
  const totalMinutes = Math.round(Number(decimalHours || 0) * 60);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hrs === 0) return `${mins} min`;
  if (mins === 0) return `${hrs} hr${hrs !== 1 ? "s" : ""}`;

  return `${hrs} hr${hrs !== 1 ? "s" : ""} ${mins} min`;
}

function formatCurrency(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  return `$${safe.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatProjectStatus(status) {
  if (!status) return null;

  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatUsageDate(dateIso) {
  if (!dateIso) return "—";

  try {
    return new Date(`${dateIso}T00:00:00`).toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateIso;
  }
}

export function ProjectDetailScreen({
  project,
  session,
  currentUser,
  onBack,
  onEditProject,
  onGenerateInvoice,
  onOpenInvoice,
  preview = null,
  initialTarget = null,
  returnLabel = "",
  onOpenProjectTasks,
  onOpenTimeLog,
}) {
  const { can, actor, orgPolicies } = usePermissions();
  const showFinancial =
    Boolean(preview?.showFinancial) || can(CAPABILITY.PROJECT_DETAIL_FINANCIAL);
  const mayManageMaterials =
    Boolean(preview?.mayManageMaterials) || can(CAPABILITY.MATERIAL_USAGE_MANUAL);
  const mayRecordMaterials =
    mayManageMaterials || can(CAPABILITY.SESSION_MATERIALS_COMMIT);
  const showOperationalMaterials = mayRecordMaterials && !showFinancial;
  const mayEditProject = Boolean(preview?.mayEdit) || can(CAPABILITY.PROJECT_EDIT);
  const mayViewMaterialPricing = can(CAPABILITY.MATERIAL_CATALOG_VIEW_PRICING);

  const [loading, setLoading] = useState(() => !preview);
  const [saving, setSaving] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [projectInvoices, setProjectInvoices] = useState(() => preview?.projectInvoices || []);
  const [projectInvoicesLoading, setProjectInvoicesLoading] = useState(false);
  const [projectInvoicesError, setProjectInvoicesError] = useState("");
  const mayViewInvoices = can(CAPABILITY.INVOICE_VIEW);
  const [projectRecord, setProjectRecord] = useState(() => preview?.projectRecord || null);
  const [usageRows, setUsageRows] = useState(() => preview?.usageRows || []);
  const [employeeNames, setEmployeeNames] = useState(() => preview?.employeeNames || {});
  const [labourLogs, setLabourLogs] = useState(() => preview?.labourLogs || []);
  const [otherCostRows, setOtherCostRows] = useState(() => preview?.otherCostRows || []);
  const [pictureCount, setPictureCount] = useState(() => preview?.pictureCount ?? 0);
  const [showProjectPictures, setShowProjectPictures] = useState(false);
  const [projectTasks, setProjectTasks] = useState(() => preview?.tasks || []);
  const [showProjectTasks, setShowProjectTasks] = useState(false);
  const [showProjectConversations, setShowProjectConversations] = useState(false);
  const [tasksAvailable, setTasksAvailable] = useState(true);
  const [clientShare, setClientShare] = useState(() => preview?.clientShare ?? null);
  const [showClientView, setShowClientView] = useState(false);
  const [clientViewAvailable, setClientViewAvailable] = useState(true);
  const [taskLocations, setTaskLocations] = useState(() => preview?.locations || []);
  // Returning from a labour entry (Time Log editor) reopens Labour Breakdown.
  const [opsPane, setOpsPane] = useState(() =>
    preview?.opsPane || (initialTarget?.kind === "labour" ? "labour" : null),
  );
  const [activeLocationId, setActiveLocationId] = useState(() =>
    initialTarget && (initialTarget.kind === "location" || initialTarget.kind === "feature")
      ? initialTarget.locationId || ""
      : "",
  );
  const initialTargetHandled = useRef(false);
  const materialSaveKeyRef = useRef("");
  const materialSaveFlight = useRef(false);
  // Planning deep link (task / location / feature); cleared on the first Back so later visits are normal.
  const [deepLink, setDeepLink] = useState(initialTarget);
  const [labourView, setLabourView] = useState(() =>
    initialTarget?.kind === "labour" && initialTarget.labourView === "employee" ? "employee" : "date",
  );
  const [expandedMaterialGroups, setExpandedMaterialGroups] = useState(
    () => preview?.expandedMaterialGroups || {},
  );
  const [materials, setMaterials] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [usageToVoid, setUsageToVoid] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [financialData, setFinancialData] = useState(() => preview?.financialData || null);
  const [financialLoading, setFinancialLoading] = useState(false);

  const [addMaterialId, setAddMaterialId] = useState("");
  const [addQuantity, setAddQuantity] = useState("");
  const [addUsageDate, setAddUsageDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [addNotes, setAddNotes] = useState("");

  const projectId = project?.project_id || projectRecord?.project_id || null;

  useEffect(() => {
    if (preview || !projectId || opsPane !== "invoicing" || !mayViewInvoices) return undefined;
    let cancelled = false;
    setProjectInvoicesLoading(true);
    setProjectInvoicesError("");
    loadInvoices(actor)
      .then((rows) => {
        if (cancelled) return;
        setProjectInvoices((rows || []).filter((row) => row.project_id === projectId));
      })
      .catch((error) => {
        if (!cancelled) {
          setProjectInvoicesError(error?.message || "Unable to load project invoices.");
        }
      })
      .finally(() => {
        if (!cancelled) setProjectInvoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [actor, mayViewInvoices, opsPane, preview, projectId]);

  useEffect(() => {
    if (initialTargetHandled.current || !initialTarget || !projectRecord) return;
    initialTargetHandled.current = true;
    if (
      (initialTarget.kind === "task" || initialTarget.kind === "tasks") &&
      tasksAvailable &&
      can(CAPABILITY.PROJECT_TASK_VIEW)
    ) {
      // Same path as tapping Tasks: it loads the project's locations first, so the
      // task resolves its real location instead of falling into "Other location".
      openTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTarget, projectRecord, tasksAvailable, can]);
  const favorite = useProjectFavorite(actor, projectId);
  const isDesktop = useIsDesktopViewport();

  // Desktop overview names task locations; reuse the same (offline-capable) structure loader.
  useEffect(() => {
    if (!isDesktop || preview || !projectId || shouldSkipSupabase(actor)) return undefined;
    let cancelled = false;
    loadProjectStructure(actor, projectId)
      .then((rows) => {
        if (!cancelled) setTaskLocations(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [actor, isDesktop, preview, projectId]);

  const refreshPictureCount = useCallback(() => {
    if (!projectId || preview || shouldSkipSupabase(actor)) return Promise.resolve();
    return loadProjectPictureSummaries(actor, projectId)
      .then(async (summary) => {
        const local = await countPendingPictures(actor, projectId).catch(() => 0);
        setPictureCount(summary.total + local);
      })
      .catch(async () => {
        const local = await countPendingPictures(actor, projectId).catch(() => 0);
        setPictureCount(local);
      });
  }, [actor, preview, projectId]);

  const refreshTasks = useCallback(
    async (resolvedProjectId) => {
      if (!resolvedProjectId || preview || shouldSkipSupabase(actor)) return;
      try {
        const result = await loadProjectTasks(actor, resolvedProjectId);
        setProjectTasks(result.tasks);
      } catch (error) {
        // Tasks tables not migrated in this environment: hide the row.
        if (isTasksUnavailableError(error)) setTasksAvailable(false);
      }
    },
    [actor, preview],
  );

  const refreshClientShare = useCallback(
    async (resolvedProjectId) => {
      if (!resolvedProjectId || preview || shouldSkipSupabase(actor)) return;
      if (!can(CAPABILITY.PROJECT_CLIENT_VIEW_MANAGE)) return;
      try {
        setClientShare(await getProjectShare(actor, resolvedProjectId));
      } catch (error) {
        // Client View migration not applied in this environment: hide the row.
        if (isMissingRpcError(error)) setClientViewAvailable(false);
      }
    },
    [actor, can, preview],
  );

  // Project Summary aggregates by project_id — never by personnel hierarchy.
  const labourTotals = useMemo(
    () => buildLabourTotals(labourLogs),
    [labourLogs],
  );
  const totalHours = labourTotals.totalHours;

  // Project → Labour is the Time Logs language scoped to this project. Hours
  // and cost come from the same labourCost helpers the summary uses.
  const labourDisplay = useMemo(() => {
    const costById = new Map();
    const logs = labourLogs.map((log) => {
      const cost = computeLineLabourCost(log);
      costById.set(log.id, {
        cost,
        rate: Number(log.hourly_rate || 0),
        rateMissing: isRateMissing(log),
      });
      return { ...log, total_hours: normalizeLogHours(log) };
    });
    const byEmployee = labourTotals.groups.map((group) => [
      group.employeeName,
      logs.filter(
        (log) => (log.supabase_user_id || log.employee || "unknown") === group.employeeKey,
      ),
    ]);
    return { logs, costById, byEmployee };
  }, [labourLogs, labourTotals.groups]);

  const projectWorkStats = useMemo(() => {
    const dates = labourLogs
      .map((row) => row.date_iso)
      .filter(Boolean)
      .sort();

    return {
      sessionCount: labourLogs.length,
      firstShiftDate: dates[0] || null,
      lastShiftDate: dates.length ? dates[dates.length - 1] : null,
    };
  }, [labourLogs]);

  const materialGroups = useMemo(
    () => groupProjectMaterialUsageForDisplay(usageRows),
    [usageRows],
  );

  const selectedMaterial = useMemo(
    () => materials.find((item) => item.material_id === addMaterialId),
    [materials, addMaterialId],
  );

  const quoteLineById = useMemo(() => {
    const map = new Map();
    (financialData?.quoteLines || []).forEach((line) => {
      map.set(line.line_snapshot_id, line);
    });
    return map;
  }, [financialData?.quoteLines]);

  const previewLineCost = useMemo(() => {
    const qty = Number(addQuantity || 0);
    const rate = Number(selectedMaterial?.unit_cost || 0);
    if (!qty || qty <= 0) return 0;
    return Math.round(qty * rate * 100) / 100;
  }, [addQuantity, selectedMaterial]);

  const loadMaterialsCatalog = useCallback(async () => {
    const relation = getMaterialRelationName(actor, orgPolicies);
    const { materials, error } = await fetchAndCacheMaterialsCatalog(supabase, {
      relation,
      actor,
    });

    if (error) {
      console.error("MATERIALS LOAD ERROR:", error);
      return;
    }

    const rows = mayViewMaterialPricing
      ? materials || []
      : (materials || []).map(stripMaterialPricingFields);
    setMaterials(rows);
  }, [actor, mayViewMaterialPricing, orgPolicies]);

  const resolveProjectRecord = useCallback(async () => {
    const projectId = project?.project_id || "";
    if (!projectId && !project?.name) {
      setLoadError("No project selected.");
      return null;
    }

    try {
      const { record, fromDevice } = await loadFieldProjectRecord(actor, projectId, async () => {
        const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
        const relation = getProjectsRelationName(actor);
        const projectSelect = canAccessProjectFinancialData(actor)
          ? PROJECT_SELECT_FINANCIAL
          : PROJECT_SELECT_OPERATIONAL;

        let query = supabase.from(relation).select(projectSelect);
        query = projectId
          ? query.eq("project_id", projectId)
          : query.eq("project_name", project.name);
        query = applyOrganizationScope(query, organizationId, {
          tableName: "projects",
        });

        const { data, error } = await query.maybeSingle();
        if (error) throw error;
        return data;
      });

      if (!record) {
        setLoadError(
          fromDevice || isOffline()
            ? "This project is not saved on this device."
            : "Project not found.",
        );
        return null;
      }

      return record;
    } catch (error) {
      console.error("PROJECT LOAD ERROR:", error);
      setLoadError(error.message || "Unable to load project.");
      return null;
    }
  }, [project, actor]);

  const loadMaterialUsage = useCallback(async (resolvedProjectId) => {
    try {
      const rows = await loadMaterialUsageForProject(resolvedProjectId, { actor });
      const enrichedRows = await attachTimelogEntryDates(rows, actor);
      setUsageRows(enrichedRows);

      const enteredByIds = [
        ...new Set(rows.map((row) => row.entered_by).filter(Boolean)),
      ];

      if (enteredByIds.length === 0) {
        setEmployeeNames({});
        return;
      }

      const users = await loadUsersByAuthIdsForOrg(
        supabase,
        actor,
        enteredByIds,
        "supabase_user_id, employee_name",
      );

      const nameMap = {};
      users.forEach((user) => {
        nameMap[user.supabase_user_id] = user.employee_name;
      });
      setEmployeeNames(nameMap);
    } catch (error) {
      console.error("MATERIAL USAGE LOAD ERROR:", error);
      setLoadError(error.message || "Unable to load material usage.");
      setUsageRows([]);
    }
  }, [actor]);

  const loadLabourLogs = useCallback(async (resolvedProjectId) => {
    try {
      const rows = await loadLabourForProject(resolvedProjectId, { actor });
      setLabourLogs(rows);
    } catch (error) {
      console.error("LABOUR COST LOAD ERROR:", error);
      setLabourLogs([]);
    }
  }, [actor]);

  const loadOtherCosts = useCallback(async (resolvedProjectId) => {
    if (!resolvedProjectId || !showFinancial) {
      setOtherCostRows([]);
      return;
    }

    try {
      const rows = await loadProjectOtherCosts(actor, resolvedProjectId);
      setOtherCostRows(rows);
    } catch (error) {
      console.error("OTHER COSTS LOAD ERROR:", error);
      setOtherCostRows([]);
      setLoadError(error.message || "Unable to load other costs.");
    }
  }, [actor, showFinancial]);

  const loadFinancialData = useCallback(async (resolvedProjectId) => {
    if (!resolvedProjectId || !showFinancial) {
      setFinancialData(null);
      return;
    }

    setFinancialLoading(true);
    try {
      const data = await loadProjectFinancialSummary(actor, resolvedProjectId);
      setFinancialData(data);
    } catch (error) {
      console.error("PROJECT FINANCIAL SUMMARY ERROR:", error);
      setFinancialData(null);
    } finally {
      setFinancialLoading(false);
    }
  }, [actor, showFinancial]);

  const loadProjectData = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    if (shouldSkipSupabase(actor)) {
      setProjectRecord(null);
      setUsageRows([]);
      setLabourLogs([]);
      setOtherCostRows([]);
      setFinancialData(null);
      setPictureCount(0);
      setLoading(false);
      return;
    }

    const record = await resolveProjectRecord();
    setProjectRecord(record);

    if (!record?.project_id) {
      setLoading(false);
      return;
    }

    if (isOffline()) {
      const local = await countOfflineProjectPictures(actor, record.project_id).catch(() => 0);
      setPictureCount(local);
      await refreshTasks(record.project_id);
      setLabourLogs([]);
      setUsageRows([]);
      setOtherCostRows([]);
      setFinancialData(null);
      setLoading(false);
      return;
    }

    const loaders = [
      refreshTasks(record.project_id),
      refreshClientShare(record.project_id),
      loadLabourLogs(record.project_id),
      loadProjectPictureSummaries(actor, record.project_id)
        .then(async (summary) => {
          const local = await countPendingPictures(actor, record.project_id).catch(() => 0);
          setPictureCount(summary.total + local);
        })
        .catch(async () => {
          const local = await countPendingPictures(actor, record.project_id).catch(() => 0);
          setPictureCount(local);
        }),
    ];

    if (showFinancial || mayRecordMaterials) {
      loaders.push(loadMaterialUsage(record.project_id));
    }

    if (mayRecordMaterials) {
      loaders.push(loadMaterialsCatalog());
    }

    if (showFinancial) {
      loaders.push(loadFinancialData(record.project_id));
      loaders.push(loadOtherCosts(record.project_id));
    }

    await Promise.all(loaders);

    setLoading(false);
  }, [
    resolveProjectRecord,
    loadMaterialUsage,
    loadLabourLogs,
    loadMaterialsCatalog,
    loadFinancialData,
    loadOtherCosts,
    refreshTasks,
    refreshClientShare,
    showFinancial,
    mayRecordMaterials,
    actor,
  ]);

  useEffect(() => {
    if (preview) return;
    loadProjectData();
  }, [loadProjectData, preview]);

  function openAddMaterial() {
    materialSaveKeyRef.current = crypto.randomUUID();
    materialSaveFlight.current = false;
    setAddMaterialId("");
    setAddQuantity("");
    setAddUsageDate(new Date().toISOString().split("T")[0]);
    setAddNotes("");
    setShowAddModal(true);
  }

  function toggleMaterialGroup(groupKey) {
    setExpandedMaterialGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  }

  async function handleSaveMaterial() {
    if (blockDemoSave(actor)) {
      return;
    }

    if (!projectId) {
      alert("Project not found.");
      return;
    }

    if (!session?.user?.id) {
      alert("You must be signed in to record material usage.");
      return;
    }

    if (!addMaterialId) {
      alert("Select a material.");
      return;
    }

    const quantity = Number(addQuantity);
    if (!quantity || quantity <= 0) {
      alert("Enter a quantity greater than zero.");
      return;
    }

    if (!selectedMaterial) {
      alert("Selected material not found.");
      return;
    }

    if (materialSaveFlight.current) return;
    materialSaveFlight.current = true;
    setSaving(true);

    try {
      await insertManualMaterialUsage({
        actor,
        projectId,
        material: selectedMaterial,
        quantity,
        enteredBy: session.user.id,
        usageDate: addUsageDate,
        notes: addNotes,
        idempotencyKey: materialSaveKeyRef.current || crypto.randomUUID(),
      });
    } catch (error) {
      console.error("MATERIAL USAGE SAVE ERROR:", error);
      alert(error.message || "Unable to save material usage.");
      materialSaveFlight.current = false;
      setSaving(false);
      return;
    }

    setSaving(false);
    setShowAddModal(false);
    await loadMaterialUsage(projectId);
    await loadFinancialData(projectId);
  }

  async function handleConfirmVoid() {
    if (!usageToVoid?.usage_id || !projectId) {
      return;
    }

    setVoiding(true);

    try {
      await voidMaterialUsage(actor, usageToVoid.usage_id, session?.user?.id);
    } catch (error) {
      console.error("MATERIAL USAGE VOID ERROR:", error);
      alert(error.message || "Unable to void material usage.");
      setVoiding(false);
      return;
    }

    setVoiding(false);
    setUsageToVoid(null);
    await loadMaterialUsage(projectId);
    await loadFinancialData(projectId);
  }

  async function handleAddOtherCost(payload) {
    if (blockDemoSave(actor)) {
      return;
    }

    await addProjectOtherCost(actor, projectId, payload);
    await Promise.all([
      loadOtherCosts(projectId),
      loadFinancialData(projectId),
    ]);
  }

  async function handleVoidOtherCost(row) {
    if (blockDemoSave(actor) || !row?.other_cost_id) {
      return;
    }

    try {
      await voidProjectOtherCost(actor, row.other_cost_id);
      await Promise.all([
        loadOtherCosts(projectId),
        loadFinancialData(projectId),
      ]);
    } catch (error) {
      console.error("OTHER COST VOID ERROR:", error);
      alert(error.message || "Unable to void this cost.");
    }
  }

  const projectTitle =
    projectRecord?.project_name || project?.name || "Project";
  const clientName =
    projectRecord?.clients?.client_name || projectRecord?.client_name || null;
  const statusLabel = formatProjectStatus(projectRecord?.status);
  const costRollup = useMemo(
    () => computeActualCostFromLedgers(usageRows, labourLogs, otherCostRows),
    [usageRows, labourLogs, otherCostRows],
  );
  const labourCost = costRollup.labourCost;
  const materialCost = costRollup.materialCost;
  const otherCost = costRollup.otherCost;
  const totalProjectCost = costRollup.actualCost;
  const financialForPanel = useMemo(() => {
    if (!financialData) return null;

    return {
      ...financialData,
      materialCost,
      labourCost,
      otherCost,
      actualCost: totalProjectCost,
      margin: roundCurrency(Number(financialData.billed || 0) - totalProjectCost),
      quotedVsActual: roundCurrency(Number(financialData.quoted || 0) - totalProjectCost),
      billedVsActual: roundCurrency(Number(financialData.billed || 0) - totalProjectCost),
    };
  }, [financialData, materialCost, labourCost, otherCost, totalProjectCost]);

  const materialOptions = materials.map((item) => ({
    value: item.material_id,
    label: mayViewMaterialPricing
      ? `${item.material_name}${item.sku ? ` (${item.sku})` : ""} — ${formatCurrency(item.unit_cost)}`
      : `${item.material_name}${item.sku ? ` (${item.sku})` : ""}`,
  }));

  const picturesWritable = Boolean(projectRecord && !isProjectArchived(projectRecord));

  async function openTasks() {
    // Locations feed the Tasks location filter / picker; works offline from
    // the device snapshot like the Locations section.
    try {
      if (!preview) setTaskLocations(await loadProjectStructure(actor, projectId));
    } catch {
      // keep whatever we had
    }
    setShowProjectTasks(true);
  }

  async function openConversations() {
    try {
      if (!preview) setTaskLocations(await loadProjectStructure(actor, projectId));
    } catch {
      // Location matching stays empty when structure cannot be loaded.
    }
    setShowProjectConversations(true);
  }
  const structureEditable = Boolean(mayEditProject && picturesWritable);

  // A background project reload (Android resumes the app when the camera or
  // gallery closes) must not unmount Pictures. Unmounting dropped the
  // post-save note checkpoint and returned to the project home.
  if (projectRecord && showProjectPictures && projectId) {
    return (
      <ProjectPicturesScreen
        projectId={projectId}
        projectName={projectTitle}
        editable={structureEditable}
        writable={picturesWritable}
        onLibraryChanged={refreshPictureCount}
        onBack={() => {
          setShowProjectPictures(false);
          refreshPictureCount();
        }}
      />
    );
  }

  if (projectRecord && showProjectTasks && projectId) {
    return (
      <ProjectTasksScreen
        projectId={projectId}
        projectName={projectTitle}
        locations={taskLocations}
        writable={picturesWritable}
        onChanged={setProjectTasks}
        initialTaskId={deepLink?.kind === "task" ? deepLink.id : null}
        callerBackLabel={deepLink?.kind === "task" ? returnLabel : ""}
        onOpenProjectTasks={onOpenProjectTasks}
        // Opened from Planning: Back from that task returns to Planning, not to this task list.
        onDeepLinkReturn={deepLink?.kind === "task" ? () => { setDeepLink(null); onBack?.(); } : undefined}
        onBack={() => {
          setDeepLink(null);
          setShowProjectTasks(false);
        }}
      />
    );
  }

  if (projectRecord && showProjectConversations && projectId) {
    return (
      <ProjectConversationsScreen
        actor={actor}
        projectId={projectId}
        projectName={projectTitle}
        clientId={projectRecord.client_id || null}
        locations={taskLocations}
        onTasksCreated={() => refreshTasks(projectId)}
        onBack={() => setShowProjectConversations(false)}
      />
    );
  }

  if (projectRecord && showClientView && projectId) {
    return (
      <ClientViewManageScreen
        projectId={projectId}
        projectName={projectTitle}
        writable={picturesWritable}
        onChanged={setClientShare}
        onBack={() => setShowClientView(false)}
      />
    );
  }

  if (projectRecord && activeLocationId) {
    return (
      <ProjectLocationWorkspace
        projectId={projectId}
        locationId={activeLocationId}
        editable={structureEditable}
        writable={picturesWritable}
        onPicturesChanged={refreshPictureCount}
        projectName={projectTitle}
        onTasksChanged={setProjectTasks}
        initialFeatureId={deepLink?.kind === "feature" ? deepLink.id : ""}
        onBack={() => {
          const fromPlanning = Boolean(deepLink);
          setDeepLink(null);
          setActiveLocationId("");
          if (fromPlanning) onBack?.(); // opened from Planning → back to Planning
          refreshPictureCount();
        }}
      />
    );
  }

  const paneTitles = {
    activity: "Activity",
    labour: "Labour",
    materials: "Materials",
    other: "Other Costs",
    invoicing: "Invoicing",
  };
  const barTitle = opsPane ? paneTitles[opsPane] || projectTitle : "Projects";
  const sumCost = (logs) =>
    logs.reduce((sum, log) => sum + (labourDisplay.costById.get(log.id)?.cost || 0), 0);
  const labourRowProps = { showProject: false, showStatus: false };

  return (
    <div
      className={`screen nx-screen rec-page${opsPane ? "" : " pj-page--home"}`}
      data-testid="project-detail"
    >
      <header className="rec-bar rec-bar--project">
        <button
          type="button"
          className="rec-bar__btn"
          onClick={opsPane ? () => setOpsPane(null) : onBack}
          aria-label="Back"
        >
          <RecIcon name="back" />
        </button>
        <div className="rec-bar__title">{barTitle}</div>
        <div className="rec-bar__side">
          {!opsPane && projectId && !preview && actor?.supabase_user_id ? (
            <button
              type="button"
              className={`pj-bar-star${favorite.favorite ? " is-on" : ""}`}
              data-testid="project-favorite-toggle"
              aria-pressed={favorite.favorite}
              aria-label={
                favorite.favorite
                  ? `Remove favorite ${projectTitle}`
                  : `Favorite ${projectTitle}`
              }
              onClick={() => favorite.toggle()}
            >
              <RecIcon name="star" />
            </button>
          ) : null}
        </div>
      </header>

      {loading ? (
        <div className="pj-empty">Loading project…</div>
      ) : loadError && !projectRecord ? (
        <div className="rec-banner" role="alert">
          {loadError}
        </div>
      ) : (
        <div className="rec-body">
          {!opsPane && !isDesktop && (
            <ProjectWorkspaceHome
              title={projectTitle}
              clientName={clientName}
              projectNumber={projectRecord?.project_number || ""}
              statusLabel={statusLabel}
              offlineState={favorite.favorite ? favorite.ready : ""}
              onRetryOffline={favorite.retry}
              showEdit={Boolean(mayEditProject && onEditProject && projectId)}
              onEdit={() =>
                onEditProject({
                  project_id: projectId,
                  name: projectTitle,
                  hrs: totalHours,
                })
              }
              totalHoursLabel={formatHoursMinutes(totalHours)}
              totalHoursEmphasis={totalHours > 0}
              showFinancial={showFinancial}
              showOperationalMaterials={showOperationalMaterials}
              labourLabel={formatCurrency(labourCost)}
              materialsLabel={formatCurrency(materialCost)}
              otherCostsLabel={formatCurrency(otherCost)}
              totalLabel={formatCurrency(totalProjectCost)}
              totalEmphasis={totalProjectCost > 0}
              address={projectRecord?.site_address || ""}
              description={projectRecord?.notes || ""}
              projectId={projectId}
              editable={structureEditable}
              onOpenLocation={setActiveLocationId}
              sessionCount={projectWorkStats.sessionCount}
              pictureCount={pictureCount}
              showInvoicing={Boolean(onGenerateInvoice)}
              showClientView={
                clientViewAvailable && !preview?.hideClientView && can(CAPABILITY.PROJECT_CLIENT_VIEW_MANAGE)
              }
              clientViewLabel={
                clientShare?.is_active
                  ? `Active · ${clientShare.shared_picture_count || 0} picture${
                      clientShare.shared_picture_count === 1 ? "" : "s"
                    } shared`
                  : "Not shared"
              }
              onOpenClientView={() => setShowClientView(true)}
              showTasks={tasksAvailable && can(CAPABILITY.PROJECT_TASK_VIEW)}
              health={tasksAvailable && can(CAPABILITY.PROJECT_TASK_VIEW) ? summarizeProjectTasks(projectTasks, localTodayISO(new Date())) : null}
              targetLabel={projectRecord?.estimated_end_date ? formatTargetCompletion(projectRecord.estimated_end_date) : ""}
              tasksLabel={taskCountLabel(projectTasks)}
              onOpenTasks={openTasks}
              showConversations={tasksAvailable && can(CAPABILITY.PROJECT_TASK_VIEW)}
              onOpenConversations={openConversations}
              onOpenPictures={() => setShowProjectPictures(true)}
              onOpenActivity={() => setOpsPane("activity")}
              onOpenLabour={() => setOpsPane("labour")}
              onOpenMaterials={() => setOpsPane("materials")}
              onOpenOtherCosts={() => setOpsPane("other")}
              onOpenInvoicing={() => setOpsPane("invoicing")}
            />
          )}
          {!opsPane && isDesktop && (
            <ProjectDesktopOverview
              actor={actor}
              title={projectTitle}
              clientName={clientName}
              projectNumber={projectRecord?.project_number || ""}
              statusLabel={statusLabel}
              offlineState={favorite.favorite ? favorite.ready : ""}
              onRetryOffline={favorite.retry}
              showEdit={Boolean(mayEditProject && onEditProject && projectId)}
              onEdit={() =>
                onEditProject({
                  project_id: projectId,
                  name: projectTitle,
                  hrs: totalHours,
                })
              }
              totalHoursLabel={formatHoursMinutes(totalHours)}
              showFinancial={showFinancial}
              showOperationalMaterials={showOperationalMaterials}
              labourLabel={formatCurrency(labourCost)}
              materialsLabel={formatCurrency(materialCost)}
              otherCostsLabel={formatCurrency(otherCost)}
              totalLabel={formatCurrency(totalProjectCost)}
              targetDate={projectRecord?.estimated_end_date || ""}
              address={projectRecord?.site_address || ""}
              description={projectRecord?.notes || ""}
              projectId={projectId}
              editable={structureEditable}
              onOpenLocation={setActiveLocationId}
              tasks={projectTasks}
              structure={taskLocations}
              tasksEnabled={tasksAvailable && can(CAPABILITY.PROJECT_TASK_VIEW)}
              logs={labourLogs}
              sessionCount={projectWorkStats.sessionCount}
              pictureCount={pictureCount}
              showTasks={tasksAvailable && can(CAPABILITY.PROJECT_TASK_VIEW)}
              onOpenTasks={openTasks}
              showConversations={tasksAvailable && can(CAPABILITY.PROJECT_TASK_VIEW)}
              onOpenConversations={openConversations}
              showClientView={
                clientViewAvailable && !preview?.hideClientView && can(CAPABILITY.PROJECT_CLIENT_VIEW_MANAGE)
              }
              clientViewLabel={
                clientShare?.is_active
                  ? `Active · ${clientShare.shared_picture_count || 0} picture${
                      clientShare.shared_picture_count === 1 ? "" : "s"
                    } shared`
                  : "Not shared"
              }
              onOpenClientView={() => setShowClientView(true)}
              showInvoicing={Boolean(onGenerateInvoice)}
              onOpenPictures={() => setShowProjectPictures(true)}
              onOpenActivity={() => setOpsPane("activity")}
              onOpenLabour={() => setOpsPane("labour")}
              onOpenMaterials={() => setOpsPane("materials")}
              onOpenOtherCosts={() => setOpsPane("other")}
              onOpenInvoicing={() => setOpsPane("invoicing")}
            />
          )}

          {opsPane === "activity" && (
            <section className="rec-card pj-list-card">
              {[
                ["Sessions", String(projectWorkStats.sessionCount)],
                ["First Shift", formatUsageDate(projectWorkStats.firstShiftDate)],
                ["Most Recent Shift", formatUsageDate(projectWorkStats.lastShiftDate)],
              ].map(([label, value]) => (
                <div key={label} className="pj-total">
                  <span>{label}</span>
                  <b>{value}</b>
                </div>
              ))}
            </section>
          )}

          {opsPane === "labour" && showFinancial && (
            <div data-testid="project-labour-detail" className="pj-pane">
              <section className="rec-card pj-paneSummary">
                <div className="pj-paneSummary__item">
                  <span className="pj-paneSummary__label">Total Hours</span>
                  <span className="pj-paneSummary__value">
                    {formatHoursMinutes(labourTotals.totalHours)}
                  </span>
                </div>
                <div className="pj-paneSummary__item pj-paneSummary__item--end">
                  <span className="pj-paneSummary__label">Labour</span>
                  <span className="pj-paneSummary__value pj-paneSummary__value--gold">
                    {formatCurrency(labourCost)}
                  </span>
                </div>
              </section>

              {labourTotals.groups.length === 0 ? (
                <p className="pj-empty">No labour recorded for this project yet.</p>
              ) : (
                <>
                  <div className="tl-scope" role="tablist" aria-label="Group labour">
                    {[
                      ["date", "By Date"],
                      ["employee", "By Employee"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={labourView === value}
                        className={`tl-scope__tab${labourView === value ? " is-on" : ""}`}
                        onClick={() => setLabourView(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <TimeLogList
                    logs={labourDisplay.logs}
                    groupByEmployee={labourView === "employee" ? labourDisplay.byEmployee : null}
                    rowProps={{ ...labourRowProps, showEmployee: labourView === "date" }}
                    onOpen={
                      onOpenTimeLog
                        ? (log) => log?.id != null && onOpenTimeLog(log, { labourView })
                        : undefined
                    }
                    rowPropsFor={(log) => {
                      const line = labourDisplay.costById.get(log.id) || {};
                      return {
                        amount: formatCurrency(line.cost),
                        detail: line.rateMissing
                          ? "Rate Missing"
                          : `${formatCurrency(line.rate)}/hr`,
                      };
                    }}
                    dayExtra={(group) => formatCurrency(sumCost(group.logs))}
                    employeeExtra={(_name, logs) => formatCurrency(sumCost(logs))}
                  />
                </>
              )}
            </div>
          )}

          {opsPane === "materials" && (showFinancial || mayRecordMaterials) && (
            <div data-testid="project-materials-detail" className="pj-pane">
              <section className="rec-card pj-paneSummary">
                <div className="pj-paneSummary__item">
                  <span className="pj-paneSummary__label">Materials Used</span>
                  <span className="pj-paneSummary__value">
                    {materialGroups.length} {materialGroups.length === 1 ? "item" : "items"}
                  </span>
                </div>
                {showFinancial ? (
                  <div className="pj-paneSummary__item pj-paneSummary__item--end">
                    <span className="pj-paneSummary__label">Total Material Cost</span>
                    <span className="pj-paneSummary__value pj-paneSummary__value--gold">
                      {formatCurrency(materialCost)}
                    </span>
                  </div>
                ) : null}
              </section>

              {mayRecordMaterials && (
                <div className="pj-pane-actions">
                  <button
                    type="button"
                    className="mat-act mat-act--add"
                    aria-label="+ Add Material"
                    onClick={openAddMaterial}
                  >
                    <RecIcon name="plus" />
                    <span>Add Material</span>
                  </button>
                </div>
              )}

              {usageRows.length === 0 ? (
                <p className="pj-empty">No materials recorded for this project yet.</p>
              ) : (
                <div data-testid="project-materials-used">
                  {materialGroups.map((group) => {
                    const open = Boolean(expandedMaterialGroups[group.groupKey]);
                    return (
                      <div
                        key={group.groupKey}
                        className={`pj-matgroup${open ? " is-open" : ""}`}
                        data-testid="project-material-group"
                        data-material-id={group.material_id || ""}
                      >
                        <button
                          type="button"
                          className="pj-matgroup__head"
                          aria-expanded={open}
                          onClick={() => toggleMaterialGroup(group.groupKey)}
                        >
                          <RecThumb
                            name={group.material_name_snapshot}
                            type="Product"
                            size="sm"
                          />
                          <span className="pj-matgroup__text">
                            <span className="pj-matgroup__name">
                              {group.material_name_snapshot}
                            </span>
                            <span className="pj-matgroup__sub">
                              {group.quantity} {group.unit_of_measure_snapshot || "units"}
                              {group.rows.length > 1 ? ` · ${group.rows.length} usages` : ""}
                            </span>
                          </span>
                          {showFinancial ? (
                            <span className="pj-matgroup__cost">
                              {formatCurrency(group.total_cost)}
                            </span>
                          ) : null}
                          <RecIcon name="chevron" className="pj-matgroup__chev" />
                        </button>

                        {open &&
                          group.rows.map((row) => (
                            <div
                              key={row.usage_id}
                              className="pj-usage"
                              data-testid="project-material-usage-row"
                              data-usage-id={row.usage_id}
                            >
                              <div className="pj-usage__top">
                                <span>
                                  {formatUsageDate(row.usage_date || row.time_log_date_iso)}
                                  {row.sku_snapshot ? ` · SKU ${row.sku_snapshot}` : ""}
                                </span>
                                {showFinancial ? <span>{formatCurrency(row.total_cost)}</span> : null}
                              </div>
                              <div className="pj-usage__line">
                                {Number(row.quantity)} {row.unit_of_measure_snapshot || "units"}
                                {showFinancial
                                  ? ` @ ${formatCurrency(row.unit_cost_snapshot)} each`
                                  : ""}
                                {row.notes ? ` · ${row.notes}` : ""}
                              </div>
                              {showFinancial &&
                                row.quote_line_snapshot_id &&
                                quoteLineById.get(row.quote_line_snapshot_id) && (
                                  <div className="pj-usage__quote">
                                    Linked quote:{" "}
                                    {quoteLineById.get(row.quote_line_snapshot_id).description_snapshot}
                                  </div>
                                )}
                              <div className="pj-usage__foot">
                                <div>
                                  <div>Entered by {employeeNames[row.entered_by] || "Unknown"}</div>
                                  {row.time_log_id ? (
                                    <div>
                                      Time log {row.time_log_id}
                                      {row.time_log_date_iso
                                        ? ` · ${formatUsageDate(row.time_log_date_iso)}`
                                        : ""}
                                    </div>
                                  ) : null}
                                  {row.session_id ? <div>Session {row.session_id}</div> : null}
                                </div>
                                {mayManageMaterials && (
                                  <button
                                    type="button"
                                    className="pj-void"
                                    onClick={() => setUsageToVoid(row)}
                                    aria-label={`Void ${row.material_name_snapshot}`}
                                  >
                                    Void
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {opsPane === "other" && showFinancial && (
            <ProjectOtherCostsPane
              rows={otherCostRows}
              editable={structureEditable}
              onAdd={handleAddOtherCost}
              onVoid={handleVoidOtherCost}
            />
          )}

          {!opsPane && showFinancial && (
            <ProjectFinancialPanel financialData={financialForPanel} loading={financialLoading} />
          )}

          {opsPane === "invoicing" && (onGenerateInvoice || onOpenInvoice) && (
            <section className="rec-card">
              <div className="rec-label">
                <RecIcon name="invoice" />
                Project Invoices
              </div>
              {projectInvoicesError ? (
                <div className="rec-banner" role="alert">{projectInvoicesError}</div>
              ) : null}
              {projectInvoicesLoading ? <p className="pj-empty">Loading invoices…</p> : null}
              {!projectInvoicesLoading && projectInvoices.length === 0 ? (
                <p style={{ margin: "0 0 14px", color: "var(--rec-muted)", fontSize: "0.92rem" }}>
                  No invoices for this project yet.
                </p>
              ) : (
                <div style={{ marginBottom: "14px" }}>
                  {projectInvoices.map((invoice) => (
                    <InvoiceListCard
                      key={invoice.invoice_id}
                      invoice={invoice}
                      onOpen={(row) => onOpenInvoice?.(row)}
                    />
                  ))}
                </div>
              )}
              {onGenerateInvoice ? (
                <>
                  <p style={{ margin: "0 0 14px", color: "var(--rec-muted)", fontSize: "0.92rem" }}>
                    Create a draft invoice from this project&apos;s logged labour and materials.
                  </p>
                  <button
                    type="button"
                    className="rec-btn rec-btn--primary"
                    style={{ width: "100%" }}
                    disabled={generatingInvoice || loading}
                    onClick={async () => {
                      if (!projectId || !onGenerateInvoice) return;
                      setGeneratingInvoice(true);
                      setLoadError("");
                      try {
                        await onGenerateInvoice(projectId);
                      } catch (error) {
                        setLoadError(error?.message || "Unable to generate invoice.");
                      } finally {
                        setGeneratingInvoice(false);
                      }
                    }}
                  >
                    {generatingInvoice ? "Generating Invoice…" : "Generate Invoice"}
                  </button>
                </>
              ) : null}
            </section>
          )}
          <div className="pj-pagepad" aria-hidden="true" />
        </div>
      )}

      {usageToVoid && (
        <ViewportPortal>
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => !voiding && setUsageToVoid(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "440px",
              background: "#0b1220",
              border: "1px solid rgba(201,168,76,0.25)",
              borderRadius: "16px",
              padding: "20px",
              color: "white",
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: "12px" }}>
              Void Material Usage
            </h2>

            <p
              style={{
                margin: "0 0 20px",
                opacity: 0.85,
                lineHeight: 1.5,
              }}
            >
              Are you sure you want to void this material entry?
              <br />
              <br />
              The row will be excluded from project cost totals and invoicing,
              but the audit record is preserved.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                onClick={() => setUsageToVoid(null)}
                disabled={voiding}
                style={{
                  padding: "10px 16px",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "transparent",
                  color: "white",
                  cursor: voiding ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmVoid}
                disabled={voiding}
                style={{
                  padding: "10px 16px",
                  borderRadius: "10px",
                  border: "1px solid rgba(224,107,90,0.45)",
                  background: "rgba(224,107,90,0.2)",
                  color: "#e06b5a",
                  fontWeight: 600,
                  cursor: voiding ? "not-allowed" : "pointer",
                }}
              >
                {voiding ? "Voiding…" : "Void Entry"}
              </button>
            </div>
          </div>
        </div>
        </ViewportPortal>
      )}

      {showAddModal && (
        <ViewportPortal>
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => !saving && setShowAddModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "500px",
              maxHeight: "88vh",
              overflowY: "auto",
              background: "#0b1220",
              border: "1px solid rgba(201,168,76,0.25)",
              borderRadius: "16px",
              padding: "20px",
              color: "white",
            }}
          >
            <h2 style={{ marginTop: 0 }}>Add Material</h2>

            <CPDropdown
              label="Material"
              value={addMaterialId}
              onChange={setAddMaterialId}
              placeholder="Select material"
              options={materialOptions}
            />

            {mayViewMaterialPricing && selectedMaterial && (
              <div
                style={{
                  marginBottom: "14px",
                  opacity: 0.75,
                  fontSize: "14px",
                }}
              >
                Unit cost: {formatCurrency(selectedMaterial.unit_cost)}
                {selectedMaterial.unit_of_measure
                  ? ` / ${selectedMaterial.unit_of_measure}`
                  : ""}
              </div>
            )}

            <CPInput
              className="cp-input-group--compact"
              label="Quantity"
              type="number"
              min="0"
              step="any"
              value={addQuantity}
              onChange={setAddQuantity}
              placeholder="Enter quantity"
            />

            <CPInput
              className="cp-input-group--compact"
              label="Usage Date"
              type="date"
              value={addUsageDate}
              onChange={setAddUsageDate}
            />

            <CPTextarea
              className="cp-input-group--compact"
              label="Notes"
              rows={3}
              value={addNotes}
              onChange={setAddNotes}
              placeholder="Optional notes"
            />

            {mayViewMaterialPricing && previewLineCost > 0 && (
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: 600,
                  color: "#d4af37",
                }}
              >
                Line cost: {formatCurrency(previewLineCost)}
              </div>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                onClick={handleSaveMaterial}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Material"}
              </button>
              <button
                type="button"
                className="nx-btn"
                onClick={() => setShowAddModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        </ViewportPortal>
      )}
    </div>
  );
}
